import { useMemo } from 'react';
import {
  Button,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
} from '@wegroup/design-system';
import { useToast } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import { usePostYellowhiveSendSimulationEmail } from '@flows/src/api/yellowhive-flows/postYellowhiveSendSimulationEmail';
import { isStaging } from '@common/src/utils/globalVariables';
import useBrokerId from '@common/src/hooks/query-params/useBrokerId';
import { isCampaignsUrl } from '../../../../../../../app/src/features/authentication/constants/SocialAuth';
import { useTranslation } from 'react-i18next';
// no broker/user token needed for public resume

type FormValues = { email: string };

interface Props {
  fullWidth?: boolean;
}

const YellowhiveSelectionOverviewEmailOfferButton = ({ fullWidth }: Props) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { handleSubmit, register, reset } = useForm<FormValues>();
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const sessionId = queryParams.get('session_id') || '';
  const { data: session } = useFlowsSession(sessionId);
  const mutate = usePostYellowhiveSendSimulationEmail();
  // no broker/user token needed for public resume

  const partyId = session?.prospect?.id || '';
  const brokerId = useBrokerId();

  const actionUrl = useMemo(() => {
    if (!sessionId) return window.location.href;

    // On campaigns: re-use current overview URL and ensure deeplink is present
    if (isCampaignsUrl) {
      const url = new URL(window.location.href);
      const search = new URLSearchParams(url.search);
      search.set('deeplink', 'slotvragen');
      return `${url.origin}${url.pathname}?${search.toString()}`;
    }

    // Public resume entry: conversations root with session_id (+ optional party_id, lang)
    const socialOrigin = isStaging
      ? 'https://conversations.staging.wegroup.be'
      : 'https://conversations.wegroup.be';
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    const search = new URLSearchParams();
    search.set('session_id', sessionId);
    if (partyId) search.set('party_id', partyId);
    if (lang) search.set('lang', lang);
    if (brokerId) search.set('broker_id', brokerId);
    search.set('deeplink', 'slotvragen');
    return `${socialOrigin}/?${search.toString()}`;
  }, [sessionId, partyId, brokerId]);

  const onSend = handleSubmit(async ({ email }) => {
    if (!partyId || !sessionId) {
      const id = 'YELLOWHIVE_EMAIL_OFFER_MISSING_IDS';
      if (!toast.isActive(id))
        toast({
          id,
          status: 'error',
          description: t('YELLOWHIVE_EMAIL_OFFER_MISSING_IDS'),
        });
      return;
    }
    try {
      await mutate.mutateAsync({
        partyId,
        sessionId,
        payload: { action_url: actionUrl, emails: [email] },
      });
      toast({
        status: 'success',
        description: t('YELLOWHIVE_EMAIL_OFFER_SENT'),
      });
      onClose();
      reset();
    } catch (e) {
      toast({
        status: 'error',
        description: t('YELLOWHIVE_EMAIL_OFFER_FAILED'),
      });
    }
  });

  const emailOfferButton = (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS2590: Expression produces a union type that is too complex to represent.
    <Button
      onClick={onOpen}
      leftIcon={<Icon name="GeneralOthersMailResent" />}
      variant="secondary"
      w={fullWidth ? '100%' : undefined}
    >
      {t('YELLOWHIVE_EMAIL_OFFER_BUTTON')}
    </Button>
  );

  return (
    <>
      {emailOfferButton}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('EMAIL')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>{t('YELLOWHIVE_EMAIL_OFFER_LABEL')}</FormLabel>
              <Input
                type="email"
                placeholder={t('EMAIL-PLACEHOLDER')}
                {...register('email', { required: true })}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={onClose}>
              {t('CLOSE')}
            </Button>
            <Button onClick={onSend} isLoading={mutate.isLoading}>
              {t('SEND')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default YellowhiveSelectionOverviewEmailOfferButton;
